import { Fragment, useEffect, useRef, useState, FC } from 'react';
import { FacebookIcon } from '@ui/icons/social/facebook';
import { InstagramIcon } from '@ui/icons/social/instagram';
import { YoutubeIcon } from '@ui/icons/social/youtube';
import { LandingNavbar } from '@app/landing/landing-navbar';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { motion } from 'framer-motion';
import { 
  Sparkles, 
  ShieldCheck, 
  Share2, 
  UserPlus, 
  Upload, 
  Globe2, 
  Lock, 
  Database, 
  Users, 
  Eye, 
  Globe, 
  Zap, 
  Wifi, 
  FolderPlus, 
  Cpu, 
  FolderX, 
  ShieldOff, 
  Key, 
  HardDrive, 
  Check, 
  X, 
  ExternalLink,
  ChevronDown,
  LucideIcon,
  FolderKanban
} from 'lucide-react';
import { BillingCycleRadio } from '@common/billing/pricing-table/billing-cycle-radio';
import { PricingTable } from '@common/billing/pricing-table/pricing-table';
import {
  ContactPagePayload,
  useSubmitContactForm,
} from '@common/contact/use-submit-contact-form';
import { useForm } from 'react-hook-form';
import { Dialog } from '@ui/overlays/dialog/dialog';
import { DialogBody } from '@ui/overlays/dialog/dialog-body';
import { DialogHeader } from '@ui/overlays/dialog/dialog-header';
import { DialogTrigger } from '@ui/overlays/dialog/dialog-trigger';
import { FormTextField } from '@ui/forms/input-field/text-field/text-field';
import { useRecaptcha } from '@common/recaptcha/use-recaptcha';
import { Button } from '@ui/buttons/button';
import { Trans } from '@ui/i18n/trans';
import { Form } from '@ui/forms/form';
import { UpsellBillingCycle } from '@common/billing/pricing-table/find-best-price';
import { useProducts } from '@common/billing/pricing-table/use-products';
import { CheckCircleOutlineIcon } from '@ui/icons/material/CheckCircleOutline';
import { FullPageLoader } from '@ui/progress/full-page-loader';
import { useThemeSelector } from '@ui/themes/theme-selector-context';
import { Helmet } from '@common/seo/helmet';
import { softwareSchema } from '@app/seo/seo-schema';

export function FreeCloudStorage() {
  const query = useProducts('pricingPage');
  const [selectedCycle, setSelectedCycle] = useState<UpsellBillingCycle>('month');
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const { selectThemeTemporarily } = useThemeSelector();
  useEffect(() => {
    selectThemeTemporarily('light');
    return () => selectThemeTemporarily(null);
  }, [selectThemeTemporarily]);

  useEffect(() => {
    document.documentElement.classList.remove('dark');
    document.documentElement.classList.add('light');
  }, []);

  const steps = [
    {
      icon: UserPlus,
      number: '01',
      title: 'Sign Up Free',
      description: 'Create your account in seconds with no credit card required',
    },
    {
      icon: Upload,
      number: '02',
      title: 'Upload Uncapped Files',
      description: 'Upload files or sync folders instantly with secure, high-speed cloud storage',
    },
    {
      icon: Globe2,
      number: '03',
      title: 'Access Anywhere',
      description: 'Access your data from any device, anywhere in the world',
    },
  ];

  const problemCards = [
    {
      icon: <HardDrive className="w-24 h-24 text-white" />,
      title: 'Expensive USD Billing & Forex Surcharges',
      desc: 'Legacy cloud platforms bill in US Dollars. Every month, Indian users pay fluctuating exchange rates plus a mandatory 3.5% foreign transaction credit card fee.',
      color: 'from-[#1E3A8A] to-[#3B82F6]',
    },
    {
      icon: <ShieldOff className="w-24 h-24 text-white" />,
      title: '15GB Shared Storage Restrictions',
      desc: 'Google Drive merges your 15GB free storage across Gmail, Photos, and Docs. A few high-res client emails or photos can freeze your entire cloud workspace.',
      color: 'from-[#3B82F6] to-[#60A5FA]',
    },
    {
      icon: <FolderX className="w-24 h-24 text-white" />,
      title: 'Lossy Video Transcoding & Compression',
      desc: 'Traditional cloud providers compress video previews and compromise file integrity. SpaceByte preserves your 4K/8K video & RAW image files bit-for-bit.',
      color: 'from-[#06B6D4] to-[#3B82F6]',
    },
    {
      icon: <Cpu className="w-24 h-24 text-white" />,
      title: 'Slow Overseas Upload Latency',
      desc: 'Routing traffic through overseas servers in Singapore or Europe slows down transfers. SpaceByte connects directly to Indian IX nodes for sub-15ms latency.',
      color: 'from-[#1E3A8A] to-[#06B6D4]',
    },
    {
      icon: <FolderPlus className="w-24 h-24 text-white" />,
      title: 'Intrusive AI Scraping & Compliance Risk',
      desc: 'Legacy terms of service permit automated AI models to scan your files. SpaceByte enforces zero AI scraping and 100% DPDP Act 2023 compliance.',
      color: 'from-[#3B82F6] to-[#1E3A8A]',
    },
  ];

  const coreFeatures = [
    {
      icon: HardDrive,
      title: 'Uncapped Uploads & Transfers',
      desc: 'Engineered specifically for heavy cloud data storage demands. Upload 50GB+ master video exports, RAW photography catalogs, and full server backups with zero file caps and 100% bit-for-bit file preservation.',
      color: 'from-[#1E3A8A] to-[#3B82F6]',
    },
    {
      icon: ShieldCheck,
      title: 'Bank-Grade Encryption & Security',
      desc: 'SpaceByte delivers zero-knowledge AES-256 secure cloud storage hosted strictly in Tier-4 Indian data centers. Fully compliant with the DPDP Act 2023, your files are never scanned or used for AI training.',
      color: 'from-[#3B82F6] to-[#60A5FA]',
    },
    {
      icon: Zap,
      title: 'Lightning Fast Fiber Speed',
      desc: 'Experience the fastest cloud storage services in India. Direct peering with major domestic Internet Exchanges (IX) guarantees sub-15ms latency and full 1Gbps fiber line-speed saturation on all transfers.',
      color: 'from-[#06B6D4] to-[#3B82F6]',
    },
    {
      icon: Key,
      title: 'Transparent INR Pricing',
      desc: 'Get transparent cloud storage pricing charged directly in INR. Pay via UPI, RuPay, NetBanking, or local cards with zero foreign exchange fees, no per-seat tax, and plans starting at ₹20/month.',
      color: 'from-[#1E3A8A] to-[#06B6D4]',
    },
  ];

  interface IndustryRole {
    num: string;
    icon: LucideIcon;
    title: string;
    desc: React.ReactNode;
    url: string;
  }

  const INDUSTRY_ROLES: IndustryRole[] = [
    {
      num: "01",
      icon: FolderKanban,
      title: "Content Creators",
      url: "https://spacebyte.in/cloud-storage-for-content-creators",
      desc: (
        <>
          Store, manage, and share multi-gigabyte creative assets instantly with{" "}
          <a href="/cloud-storage-for-content-creators" className="text-[#00c6ff] font-bold hover:underline">
            Cloud Storage for Content Creators
          </a>.
        </>
      ),
    },
    {
      num: "02",
      icon: Zap,
      title: "Video Creators",
      url: "https://spacebyte.in/cloud-storage-for-video-creators",
      desc: (
        <>
          High-speed cloud storage engineered for 4K/8K ProRes, RAW video timelines, and proxy workflows with{" "}
          <a href="/cloud-storage-for-video-creators" className="text-[#00c6ff] font-bold hover:underline">
            Cloud Storage for Video Creators
          </a>.
        </>
      ),
    },
    {
      num: "03",
      icon: Globe,
      title: "Photographers",
      url: "https://spacebyte.in/cloud-storage-for-photographers",
      desc: (
        <>
          Secure online galleries and RAW image cloud storage featuring bit-for-bit preservation with{" "}
          <a href="/cloud-storage-for-photographers" className="text-[#00c6ff] font-bold hover:underline">
            Cloud Storage for Photographers
          </a>.
        </>
      ),
    },
    {
      num: "04",
      icon: Users,
      title: "YouTubers",
      url: "https://spacebyte.in/cloud-storage-for-youtubers",
      desc: (
        <>
          Rapid export uploads, thumbnail archives, and seamless team collaboration links via{" "}
          <a href="/cloud-storage-for-youtubers" className="text-[#00c6ff] font-bold hover:underline">
            Cloud Storage for YouTubers
          </a>.
        </>
      ),
    },
    {
      num: "05",
      icon: Share2,
      title: "Digital Marketing Agencies",
      url: "https://spacebyte.in/cloud-storage-for-digital-marketing",
      desc: (
        <>
          Centralized client asset management with custom branded links and permission controls using{" "}
          <a href="/cloud-storage-for-digital-marketing" className="text-[#00c6ff] font-bold hover:underline">
            Cloud Storage for Digital Marketing Agencies
          </a>.
        </>
      ),
    },
    {
      num: "06",
      icon: Key,
      title: "SEO Specialists",
      url: "https://spacebyte.in/cloud-storage-for-seo-specialists",
      desc: (
        <>
          Fast file distribution for site audit reports, bulk crawling datasets, and client deliverables with{" "}
          <a href="/cloud-storage-for-seo-specialists" className="text-[#00c6ff] font-bold hover:underline">
            Cloud Storage for SEO Specialists
          </a>.
        </>
      ),
    },
    {
      num: "07",
      icon: ShieldCheck,
      title: "Business Cloud Storage",
      url: "https://spacebyte.in/business-cloud-storage",
      desc: (
        <>
          DPDP-compliant cloud storage infrastructure built for team file sharing, access management, and privacy via{" "}
          <a href="/business-cloud-storage" className="text-[#00c6ff] font-bold hover:underline">
            Secure Business Cloud Storage
          </a>.
        </>
      ),
    },
    {
      num: "08",
      icon: Database,
      title: "Enterprise Solutions",
      url: "https://spacebyte.in/cloud-storage-for-enterprise",
      desc: (
        <>
          Scalable multi-terabyte data vaults with high availability and line-speed recovery options with{" "}
          <a href="/cloud-storage-for-enterprise" className="text-[#00c6ff] font-bold hover:underline">
            Enterprise Cloud Backup Solutions
          </a>.
        </>
      ),
    },
    {
      num: "09",
      icon: HardDrive,
      title: "Smart Google Drive Alternative",
      url: "https://spacebyte.in/smart-google-drive-alternative",
      desc: (
        <>
          Ditch USD conversion fees and 15GB shared limits for faster, cheaper domestic cloud storage with{" "}
          <a href="/smart-google-drive-alternative" className="text-[#00c6ff] font-bold hover:underline">
            Smart Google Drive Alternative
          </a>.
        </>
      ),
    },
    {
      num: "10",
      icon: Lock,
      title: "Fast Proton Drive Alternative",
      url: "https://spacebyte.in/best-proton-drive-alternative",
      desc: (
        <>
          Enjoy privacy-first zero-knowledge encryption combined with ultra-fast domestic Indian speeds via{" "}
          <a href="/best-proton-drive-alternative" className="text-[#00c6ff] font-bold hover:underline">
            Fast Proton Drive Alternative
          </a>.
        </>
      ),
    },
    {
      num: "11",
      icon: Wifi,
      title: "Secure Dropbox Alternative",
      url: "https://spacebyte.in/dropbox-alternative-fast-secure-cloud-storage",
      desc: (
        <>
          High-performance file syncing and unlimited file size uploads at a fraction of legacy costs with{" "}
          <a href="/dropbox-alternative-fast-secure-cloud-storage" className="text-[#00c6ff] font-bold hover:underline">
            Secure Dropbox Alternative
          </a>.
        </>
      ),
    },
    {
      num: "12",
      icon: HardDrive,
      title: "Best OneDrive Alternative",
      url: "https://spacebyte.in/best-onedrive-alternative",
      desc: (
        <>
          Break free from 5 GB caps, forced desktop sync, and expensive USD subscriptions with 25 GB free storage via{" "}
          <a href="/best-onedrive-alternative" className="text-[#00c6ff] font-bold hover:underline">
            Best Microsoft OneDrive Alternative
          </a>.
        </>
      ),
    },
  ];

  const faqData = [
    {
      question: "How do I fix the MEGA download limit issue?",
      answer: "MEGA download limits happen entirely because of their server-side transfer restrictions. The only true fix is switching to a secure, modern cloud storage alternative like SpaceByte. It gives you faster downloads, zero transfer quota limits, and no file size restrictions."
    },
    {
      question: "Why does MEGA block downloads sometimes?",
      answer: "MEGA blocks downloads whenever a user's IP address exceeds its allotted transfer quota limit, forcibly interrupting large file downloads. SpaceByte guarantees fast performance, no transfer limits, and reliable file access."
    },
    {
      question: "What is the best free cloud storage platform in 2026?",
      answer: "When evaluating <strong>free cloud storage 2026</strong>, SpaceByte is widely considered the <strong>best free cloud storage platform</strong>. If you are asking <strong>which provider gives the most free cloud storage</strong>, SpaceByte provides a dedicated 25GB unmetered tier, outclassing competitors.",
    },
    {
      question: "What happens if I run out of free cloud storage?",
      answer: "<strong>What happens if I run out of free cloud storage?</strong> With legacy platforms, your email might freeze. With SpaceByte, you can seamlessly scale your 25GB tier to 100GB or 1TB for a nominal fee natively via UPI.",
    },
    {
      question: "How to maximize your free cloud storage?",
      answer: "Learning <strong>how to maximize your free cloud storage</strong> involves separating heavy media from standard documents. By migrating large videos and archives to SpaceByte's unmetered 25GB vault, you can free up space on legacy platforms and maximize your available quotas without paying for upgrades.",
    },
    {
      question: "What is the best free cloud storage in 2026?",
      answer: "While many users search for <strong>onedrive free storage</strong> (which offers 5GB) or <strong>google storage plans</strong> (which provide a shared 15GB), SpaceByte is the <strong>best free cloud storage</strong> offering a massive, dedicated 25GB unmetered tier with zero AI scraping.",
    },
    {
      question: "Which cloud storage is best in India for 2026?",
      answer:
        "<strong>SpaceByte Cloud</strong> is rated as the <strong>best cloud storage in India</strong> and top <strong>best cloud drive</strong> for personal use and business. By utilizing local Tier-4 data centers and direct domestic IX peering, SpaceByte delivers sub-15ms latency, full 1Gbps+ fiber upload speeds, zero file compression, and 100% DPDP Act compliance. Transparent INR pricing starting at just ₹20/month makes it the ultimate upgrade.",
    },
    {
      question: "Why switch to SpaceByte as a Google Drive alternative instead of google drive login or my drive?",
      answer:
        "When performing a <strong>google drive login</strong> or accessing <strong>my drive</strong> / <strong>my google drive</strong> on <strong>google drive online</strong>, free accounts are capped at 15 GB shared across Gmail and Photos while Google algorithms scan private files for AI training. SpaceByte provides 25 GB permanent free storage, 100% zero AI scraping, sub-15ms domestic speed, and simple password-protected <strong>google drive link</strong> alternative sharing.",
    },
    {
      question: "Is SpaceByte cheaper than Google Drive and OneDrive?",
      answer:
        "Yes. SpaceByte offers the <strong>cheapest cloud storage</strong> pricing available in India. Unlike <strong>googledrive</strong> and <strong>onedrive</strong>, which charge in US Dollars with foreign exchange credit card markups, SpaceByte bills directly in Indian Rupees (INR) via UPI and local cards. For example, 1TB of high-speed cloud storage on SpaceByte costs just ₹150/month—saving you up to 70% compared to legacy cloud platforms.",
    },
    {
      question: "What makes secure cloud storage different from standard online storage?",
      answer:
        "Standard <strong>online storage</strong> providers often retain master encryption keys, allowing automated systems to scan your uploaded files for advertising, indexing, or AI training. SpaceByte provides true <strong>secure cloud storage</strong> featuring zero-knowledge AES-256 end-to-end encryption. Your files are encrypted before leaving your device, ensuring that nobody—not even SpaceByte engineers—can view, scan, or access your confidential data.",
    },
    {
      question: "Is SpaceByte a good alternative for mega cloud storage free limits and free cloud storage 1TB needs?",
      answer: "Yes. While users often look for a <strong>mega cloud storage free</strong> tier or <strong>free cloud storage 1TB</strong>, SpaceByte offers 25GB of permanent <strong>free cloud storage</strong> coupled with superior sub-15ms <strong>Indian cloud storage</strong> speeds. It provides the best of both worlds: generous free capacity and ultra-fast, locally hosted performance. For users needing massive capacity, our <strong>unlimited cloud storage</strong> capabilities and highly affordable 1TB plans offer the perfect upgrade path.",
    },
    {
      question: "What should I look for in free cloud storage plans?",
      answer: "When comparing <strong>cloud storage free plans</strong>, raw storage space is only half the equation. You also need to look at single-file upload caps (many cap at 5GB-15GB), file compression (some silently transcode videos or downsample photos), and network throughput (overseas vs domestic routing). SpaceByte free tiers ensure you never experience throttling or file compression.",
    },
    {
      question: "Why choosing the right cloud storage free plans matters?",
      answer: "Choosing the right plan is critical because the shift to cloud architecture means relying on distributed servers. <strong>Choosing the right cloud storage free plans matters</strong> to protect your data against ransomware, device damage, and hardware crashes while ensuring seamless accessibility.",
    },
    {
      question: "Which cloud storage is best for free and offers the most space?",
      answer: "If you are searching for <strong>which cloud storage is best for free</strong> or wondering <strong>which cloud storage is free and unlimited</strong>, it is vital to read the fine print. While some providers tempt you with claims of <strong>free cloud storage 100gb</strong>, <strong>free cloud storage 1tb</strong>, or even <strong>free 2tb cloud storage</strong>, they often throttle your download speeds or delete files after 30 days of inactivity. SpaceByte provides highly reliable, permanent <strong>free online cloud storage</strong>. It is widely considered the <strong>cloud storage best free</strong> option because your initial 25GB quota remains lightning fast and ad-free, serving as a powerful stepping stone toward <strong>free unlimited cloud storage</strong> configurations for heavy users."
    },
    {
      question: "How do I get 100GB or 1TB of cloud storage for free without scams?",
      answer: "Many users search for <strong>how do i get 100gb of cloud free</strong> or <strong>how do i get 1tb cloud storage for free</strong>. The truth is, legitimate services do not give away massive terabytes for free forever. Instead of risking your data with untrusted <strong>largest free cloud storage</strong> apps, SpaceByte offers a highly secure and guaranteed 25GB free tier, with extremely affordable upgrades when you genuinely need more space.",
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(item => ({
      "@type": "Question",
      "name": item.question.replace(/<\/?[^>]+(>|$)/g, ""),
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer.replace(/<\/?[^>]+(>|$)/g, "")
      }
    }))
  };

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  useCustomStyles();

  return (
    <Fragment>
      <Helmet>
        <title>Free Cloud Storage Plans in India: Top 15 Services Compared</title>
        <meta
          name="description"
          content="Compare free cloud storage plans in India by storage limit, privacy, speed, and expiry rules. See which option is best for your photos and backups."
        />
        <meta
          name="keywords"
          content="free cloud storage, best free cloud storage, cloud storage free plans, free cloud storage plans, 1TB free cloud storage, 2TB free cloud storage, unlimited cloud storage, mega transfer quota"
        />
        <link rel="canonical" href="https://spacebyte.in/free-cloud-storage" />
        <script type="application/ld+json" data-keep="true">
          {JSON.stringify(softwareSchema)}
        </script>
        <script type="application/ld+json" data-keep="true">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <LandingNavbar
        color="transparent"
        className="fixed top-0 z-50 w-full flex-shrink-0 border-none bg-light-primary shadow-lg"
        menuPosition="homepage-navbar"
      />

      {/* HERO SECTION */}
      <section className="relative overflow-hidden !bg-gradient-to-r !from-[#0072ff]/8 !to-[#00c6ff]/8 py-24 md:py-32">
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid-hero" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#1E3A8A" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid-hero)" />
          </svg>
        </div>

        <div className="landing-container z-10 flex flex-col items-center justify-between pb-40 pt-100 lg:flex-row">
          <div className="mx-auto w-full max-w-6xl px-4 text-center">
            
            {/* H1 Heading */}
            <h1 className="font-archivo text-[40px] md:text-[60px] font-bold leading-[1.2] tracking-[-0.02em] text-[#1f2937]">
              Best Free Cloud Storage <br />
              <span className="color-primary-gradient">Compare Storage, Limits & Features</span>
            </h1>

            {/* Subheadline */}
            <p className="mx-auto mt-30 max-w-3xl font-inter text-[18px] md:text-[20px] leading-[1.7] text-[#6b7280]">
              Looking for <strong>free cloud storage plans</strong> in India? Before falling for <strong>1TB free cloud storage</strong> traps with ads and slow speeds, compare the top services. SpaceByte offers <strong>25 GB permanent free cloud storage</strong> with zero ads, sub-15ms speeds, and 100% privacy.
            </p>


            {/* CTA Buttons */}
            <div className="my-36 pb-20 flex flex-col items-center justify-center gap-6 sm:flex-row">
              <a
                href="/register"
                className="inline-flex items-center justify-center px-28 py-10 text-white font-bold bg-gradient-to-r from-[#0072ff] to-[#00c6ff] shadow-lg transition-all duration-300 hover:from-[#0059d6] hover:to-[#00aaff] hover:shadow-2xl hover:-translate-y-1"
                style={{ borderRadius: '12px' }}
              >
                Get 25 GB Free Storage
              </a>
              <a
                href="/pricing"
                className="inline-flex items-center justify-center px-36 py-8 font-bold text-[#0072ff] relative transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                style={{
                  borderRadius: '12px',
                  background: 'linear-gradient(#e5f5fb, #e5f5fb) padding-box, linear-gradient(90deg, #0072ff, #00c6ff) border-box',
                  border: '2px solid transparent',
                }}
              >
                View Pricing Tiers
              </a>
            </div>

            {/* Hero Image */}
            <div className="mt-26 flex justify-center px-2">
              <div className="w-full max-w-5xl overflow-hidden rounded-3xl bg-white shadow-2xl">
                <div className="relative w-full max-w-5xl">
                  <img
                    src="/images/LandingImg/heroimg.webp"
                    alt="SpaceByte Cloud Storage Dashboard"
                    className="h-auto w-full object-cover rounded-2xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THREE STEPS SECTION */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-[#F8FAFC] to-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5 landing-container z-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-[#3B82F6] rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#06B6D4] rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 my-20 md:my-20 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-archivo font-bold text-[#111827] mb-10">
              How to Choose the Best Cloud Storage for Personal Use & Start in <span className="color-primary-gradient">Three Steps</span>
            </h2>
            <p className="text-lg text-[#6B7280] max-w-2xl mx-auto pb-10 font-archivo">
              Start storing and sharing your files securely in minutes
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 md:gap-12 relative landing-container">
            {/* Connecting Line precisely centered behind the 80px circle icons */}
            <div className="hidden md:block absolute top-[40px] left-[18%] right-[18%] h-1 bg-gradient-to-r from-[#3B82F6] via-[#60A5FA] to-[#06B6D4] opacity-40 z-0"></div>

            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="relative z-10"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="relative mb-6">
                    <div className="w-80 h-80 bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] rounded-full flex items-center justify-center relative z-10 shadow-lg">
                      <step.icon className="w-40 h-40 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-10 w-40 h-40 bg-white rounded-full flex items-center justify-center border-2 border-[#3B82F6] z-20">
                      <span className="text-[#1E3A8A] font-bold text-sm">{step.number}</span>
                    </div>
                  </div>

                  <h3 className="text-2xl font-semibold font-archivo text-[#111827] my-20">
                    {step.title}
                  </h3>
                  <p className="text-[#6B7280] font-archivo max-w-xs">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROBLEM WITH LEGACY PLATFORMS (SWIPER CAROUSEL) */}
      <section className="relative overflow-hidden bg-[#f5f8ff] py-24 md:py-32">
        <div className="landing-container z-10 mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="animate-fade-down font-archivo text-3xl font-bold md:text-4xl lg:text-5xl py-16 md:py-24">
              Why Users Are Moving Away <span className="color-primary-gradient">from Google Drive & OneDrive</span>
            </h2>
          </div>

          <Swiper
            modules={[Pagination]}
            spaceBetween={24}
            pagination={{ clickable: true, el: '.custom-pagination-problem' }}
            breakpoints={{
              0: { slidesPerView: 1 },
              768: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
            style={{ paddingBottom: '30px' }}
          >
            {problemCards.map((card, index) => (
              <SwiperSlide key={index} style={{ display: 'flex', height: 'auto' }}>
                <div className="group bg-white rounded-2xl p-14 md:p-16 lg:p-18 border border-gray-200 hover:border-[#3B82F6]/40 hover:shadow-xl transition-all duration-300 flex flex-col flex-1 min-h-[250px]">
                  <div className={`w-40 h-40 md:w-50 md:h-50 bg-gradient-to-br ${card.color} rounded-xl flex items-center justify-center mb-12 transition-transform duration-300 group-hover:scale-110`}>
                    {card.icon}
                  </div>
                  <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mb-8 font-archivo">
                    {card.title}
                  </h3>
                  <p className="color-gray font-base text-sm leading-relaxed flex-grow font-inter">
                    {card.desc}
                  </p>
                </div>
              </SwiperSlide>
            ))}

            <div className="custom-pagination-problem text-center mt-10 relative" />
          </Swiper>
        </div>
      </section>

      {/* DIRECT COMPARISON MATRIX SECTION */}
      <ComparisonSection />

      {/* CORE FEATURES GRID */}
      <section id="features" className="py-24 md:py-40 text-black bg-white relative overflow-hidden dark:bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 landing-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl font-archivo sm:text-3xl lg:text-5xl font-bold text-[#111827] mb-10">
              Secure Cloud Storage Built for <span className="color-primary-gradient">High-Speed Workflows</span>
            </h2>
            <p className="text-lg text-[#6B7280] font-archivo max-w-2xl mx-auto">
              Secure encrypted cloud storage with sub-15ms domestic Indian speeds, transparent INR pricing, and zero AI scraping.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-14 mt-40">
            {coreFeatures.map((feat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8, transition: { duration: 0.2 } }}
                className="group"
              >
                <div className="bg-white rounded-2xl p-12 border border-gray-200 hover:border-[#3B82F6]/30 hover:shadow-xl transition-all h-full">
                  <div className={`w-50 h-50 bg-gradient-to-br ${feat.color} rounded-xl flex items-center justify-center mb-14 group-hover:scale-110 transition-transform`}>
                    <feat.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#111827] mb-10 font-archivo">
                    {feat.title}
                  </h3>
                  <p className="text-[#6B7280] leading-relaxed mb-10 font-inter">
                    {feat.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUTIONS BY PROFESSION (INTERNAL LINKING MATRIX) */}
      <section className="py-24 md:py-32 relative overflow-hidden bg-white">
        <div className="sb-roles landing-container z-10 bg-[#f5f8ff]" id="roles">
          <div className="sb-roles-left">
            <div>
              <h2 className="font-archivo text-2xl md:text-3xl font-bold">
                Cloud Storage Solutions Engineered for Your Industry
              </h2>
              <p className="font-inter mt-4 text-sm opacity-80 leading-relaxed">
                Discover tailored high-performance cloud storage architectures built for specific creative workflows, agencies, and enterprise applications.
              </p>
            </div>
            <div className="sb-rl-count">
              11<br />
              <span style={{ fontFamily: "'Outfit',sans-serif", fontSize: '.85rem', fontWeight: 500, color: 'rgba(253,252,250,0.45)', letterSpacing: '.08em', textTransform: 'uppercase' }}>
                Solutions Linked
              </span>
            </div>
          </div>
          <div className="sb-roles-list">
            {INDUSTRY_ROLES.map((r) => (
              <div className="sb-role-row reveal" key={r.num}>
                <div className="sb-rr-num">{r.num}</div>
                <div className="sb-rr-icon">
                  <r.icon size={28} strokeWidth={2} className="text-[#1E3A8A]" />
                </div>
                <div className="sb-rr-body">
                  <div className="sb-rr-title font-archivo">{r.title}</div>
                  <div className="sb-rr-desc font-inter">{r.desc}</div>
                </div>
                <a href={r.url} target="_blank" rel="noopener noreferrer" className="sb-rr-arrow">
                  →
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING SECTION */}
      <section id="Pricing" className="pricing-section relative overflow-hidden bg-white py-40 text-black lg:py-50">
        <div className="landing-container z-10">
          <div className="mx-auto max-w-xl space-y-16 text-center">
            <h2 className="animate-fade-down font-archivo text-3xl font-bold md:text-4xl lg:text-5xl">
              Cloud Storage Pricing <span className="color-primary-gradient">That Saves Your Money</span>
            </h2>
            <p className="color-gray font-inter animate-fade-down">
              Get the <strong>cheapest cloud storage</strong> with no hidden fees, zero foreign exchange markups, zero-knowledge encryption, and line-speed access anytime. <strong>Buy cloud storage</strong> plans starting today to upgrade your digital workspace.
            </p>
          </div>

          <div className="my-40 flex flex-col md:flex-row justify-center items-center gap-16">
            <BillingCycleRadio
              products={query.data?.products}
              selectedCycle={selectedCycle}
              onChange={setSelectedCycle}
              className="input-radio animate-fade-up flex justify-center items-center flex-wrap"
              size="lg"
            />
          </div>

          <PricingTable
            selectedCycle={selectedCycle}
            productLoader="pricingPage"
            className="animate-fade-down"
          />

          {/* Premium Section */}
          <div
            className="premium-bg animate-fade-down bg-light-primary relative mt-40 flex flex-col items-center justify-between space-y-8 overflow-hidden rounded-2xl border-primary p-16 shadow-lg md:flex-row md:space-y-0 md:p-32"
            style={{
              backgroundImage: "url('/images/LandingImg/ctamask.png'), url('/images/LandingImg/ctashape.png')",
              backgroundSize: 'contain, contain',
              backgroundRepeat: 'no-repeat, no-repeat',
              backgroundPosition: 'left center, right center',
            }}
          >
            <div className="font-archivo absolute -left-54 top-16 w-160 rotate-[-45deg] transform rounded bg-warning text-center text-sm font-medium text-black md:-left-56 lg:-left-50 lg:top-20">
              Premium
            </div>

            <div className="flex flex-col items-center gap-8 space-y-8 md:flex-row md:space-x-8 md:space-y-0">
              <img
                src="/images/LandingImg/cta-icon.webp"
                className="color-primary rounded-lg bg-white p-16"
                alt="CTA Icon"
              />
              <div className="text-center md:text-left">
                <h2 className="font-archivo text-3xl font-bold">5 TB or More?</h2>
                <span className="color-primary font-archivo text-2xl font-semibold">
                  ₹0.15/GB <span className="text-lg font-normal text-black">/Monthly</span>
                </span>
                <p className="font-inter mt-8 flex items-start justify-center gap-4 text-sm md:justify-start">
                  <CheckCircleOutlineIcon size="sm" /> 100% Safe & Secure with End-to-End Encryption
                </p>
                <p className="font-inter mt-1 flex items-start justify-center gap-4 text-sm md:justify-start">
                  <CheckCircleOutlineIcon size="sm" /> Calculate the cost or Reach out to us
                </p>
              </div>
            </div>

            <div className="flex flex-row flex-wrap gap-8 sm:flex-col lg:flex-row justify-center">
              <div className="flex justify-center">{CalculateDialogTrigger()}</div>
              <div className="mt-0 flex justify-center">
                <a href="/contact" className="btn bg-[#f9fafb] border-2 border-[#0072ff] text-black hover:bg-[#0072ff] hover:text-white">
                  <span>Contact Us</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VIDEO BANNER SECTION */}
      <section className="w-full px-4 md:px-10 py-40 bg-white relative overflow-hidden">
        <div className="landing-container min-h-[280px] md:min-h-[380px] flex items-center justify-center relative rounded-2xl overflow-hidden">
          <video
            className="absolute inset-0 w-full h-full object-cover"
            src="/images/LandingImg/video.mp4"
            autoPlay
            loop
            muted
            playsInline
          />
          <div className="absolute inset-0 bg-black/60" />
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 via-transparent to-blue-600/20" />

          <div className="relative z-10 text-center max-w-4xl px-4">
            <h2 className="text-white text-3xl md:text-5xl font-semibold font-archivo">
              Your Data, Your Control, Your Cloud
            </h2>
            <p className="text-white/80 text-base md:text-lg mt-20 font-inter">
              A smarter cloud storage platform that gives you complete control over your data, file sharing, and digital privacy.
            </p>
            <div className="mt-20">
              <a href="/register" className="btn bg-white text-black px-20 py-10 rounded-xl font-medium hover:scale-105 transition">
                <span>Get 25 GB FREE</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ SECTION (AEO & PAA OPTIMIZED) */}
      <section id="faq" className="bg-[#f5f8ff] relative overflow-hidden py-24 text-black lg:py-32">
        <div className="landing-container z-10">
          <div className="section-wrapper">
            <div className="section-inner">
              {/* LEFT PANEL */}
              <div className="left-panel">
                <div className="badge font-archivo">
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <circle cx="5" cy="5" r="5" fill="#38bdf8" />
                  </svg>
                  SpaceByte Cloud
                </div>

                <h2 className="left-title font-archivo">
                  Best Cloud Storage <br />
                  Secure, Fast and <br />
                  Reliable
                </h2>

                <p className="left-sub font-archivo">
                  Everything you need to store, share, and access your files anytime, anywhere.
                </p>

                <div className="illustration">
                  <div className="illus-card">
                    <div className="illus-row">
                      <div className="illus-icon ic-blue">☁️</div>
                      <div className="illus-text font-archivo">
                        <strong>My Storage</strong>
                        <span>18.3 GB used of 25 GB</span>
                        <div className="illus-bar">
                          <div className="illus-bar-fill" style={{ width: '73%' }} />
                        </div>
                      </div>
                    </div>

                    <div className="illus-row">
                      <div className="illus-icon ic-sky">📁</div>
                      <div className="illus-text font-archivo">
                        <strong>Shared Folders</strong>
                        <span>12 folders · 3 collaborators</span>
                      </div>
                    </div>

                    <div className="illus-row">
                      <div className="illus-icon ic-white">🔒</div>
                      <div className="illus-text">
                        <strong>End-to-End Encrypted</strong>
                        <span>All files secured in transit</span>
                      </div>
                    </div>

                    <div className="stats-row">
                      <div className="stat-chip">
                        <strong>25 GB</strong>
                        <span>Free Storage</span>
                      </div>
                      <div className="stat-chip">
                        <strong>99.9%</strong>
                        <span>Uptime</span>
                      </div>
                      <div className="stat-chip">
                        <strong>24/7</strong>
                        <span>Support</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT PANEL */}
              <div className="right-panel">
                <p className="section-tag">People Also Ask</p>

                <h2 className="section-title">
                  Frequently Asked <br />
                  Questions
                </h2>

                <p className="section-desc">
                  Find answers to the most common questions about SpaceByte Cloud Storage and competitor comparisons.
                </p>

                <div className="accordion">
                  {faqData.map((item, index) => (
                    <div key={index} className={`acc-item ${openIndex === index ? 'open' : ''}`}>
                      <button className="acc-header" onClick={() => toggle(index)}>
                        <div className="acc-num">{String(index + 1).padStart(2, '0')}</div>
                        <span className="acc-label">{item.question}</span>
                        <div className="acc-chevron">
                          <svg viewBox="0 0 24 24" fill="none">
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </div>
                      </button>

                      <div className="acc-body">
                        <div className="acc-body-inner" dangerouslySetInnerHTML={{ __html: item.answer }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CONVERSION CTA */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-[#1E3A8A] via-[#3B82F6] to-[#60A5FA] relative overflow-hidden">
        <div className="absolute inset-0 landing-container z-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="cta-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#cta-grid)" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-40 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white font-archivo"
            >
              Ready to Secure Your Data?
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="text-lg sm:text-xl text-white/90 my-16 max-w-2xl mx-auto font-inter"
            >
              Join thousands of businesses trusting secure cloud storage to protect critical data.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-10 justify-center items-center"
            >
              <a
                href="/register"
                className="group bg-white text-[#1E3A8A] px-16 py-14 rounded-xl font-semibold hover:bg-gray-100 transition-all hover:shadow-2xl hover:shadow-white/20 transform hover:-translate-y-1 flex items-center gap-2 btn"
              >
                Get 25 GB FREE - No Card Needed
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="Footer-section relative overflow-hidden bg-[#0F172A] text-white pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 mb-12">
            <div className="col-span-2 md:col-span-4 lg:col-span-2">
              <div className="space-y-4 mr-12 padding-logo padding-top">
                <a href="https://spacebyte.in/" className="flex-shrink-0" aria-label="SpaceByte Homepage">
                  <img src="/images/logo-light.png" alt="Logo" className="site-logo" />
                </a>
                <div>
                  <p className="font-medium pt-8 pb-10">SPACEBYTE CLOUD STORAGE</p>
                  <p className="pb-10 pt-3 flex items-center gap-2">
                    311, Maruti Plaza, Sardar Patel Chowk, Krishnanagar, Ahmedabad, Gujarat, India - 382345
                  </p>
                  <p className="pb-10 pt-3 flex items-center gap-2 font-inter text-xs text-gray-400">
                    Mo. +91 97257 71457
                  </p>
                </div>
                <div>
                  <p className="font-bold text-base pb-10 pt-3">FOLLOW US FOR "SPACEBYTE.IN"</p>
                  <div className="flex gap-4 mt-3 logg mb-10">
                    <ul className="flex space-x-16 pt-3 pr-10">
                      <li className="bg-light rounded-lg p-8">
                        <a href="https://www.facebook.com/people/SpaceByte-Cloud/61581378077823/#" target="_blank" rel="noopener noreferrer">
                          <FacebookIcon className="text-white" />
                        </a>
                      </li>
                      <li className="bg-light rounded-lg p-8">
                        <a href="https://www.instagram.com/spacebyte.in?igsh=MmZidnprMXZtbTho" target="_blank" rel="noopener noreferrer">
                          <InstagramIcon className="text-white" />
                        </a>
                      </li>
                      <li className="bg-light rounded-lg p-8">
                        <a href="https://youtube.com/@spacebytecloudstorage?si=lt2mfOM7kSaN3g6B" target="_blank" rel="noopener noreferrer">
                          <YoutubeIcon className="text-white" />
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="py-8 md:py-20 px-30">
              <h2 className="font-semibold text-xl mb-10 text-start">Product</h2>
              <ul className="space-y-3 text-sm text-gray-400 text-start">
                <li>
                  <a href="#features" className="hover:text-white transition-colors">
                    Features
                  </a>
                </li>
                <li>
                  <a href="/pricing" className="hover:text-white transition-colors">
                    Pricing
                  </a>
                </li>
                <li>
                  <a href="#security" className="hover:text-white transition-colors">
                    Security
                  </a>
                </li>
              </ul>
            </div>

            <div className="py-8 md:py-20 px-30">
              <h2 className="font-semibold text-xl mb-10 text-start">Company</h2>
              <ul className="space-y-3 text-sm text-gray-400 text-start">
                <li>
                  <a href="/about-us" className="hover:text-white transition-colors">
                    About Us
                  </a>
                </li>
                <li>
                  <a href="/pages/articles" className="hover:text-white transition-colors">
                    Articles
                  </a>
                </li>
                <li>
                  <a href="/contact" className="hover:text-white transition-colors">
                    Contact Us
                  </a>
                </li>
              </ul>
            </div>

            <div className="py-8 md:py-20 px-30">
              <h2 className="font-semibold text-xl mb-10 text-start">Resources</h2>
              <ul className="space-y-3 text-sm text-gray-400 text-start">
                <li>
                  <a href="#faq" className="hover:text-white transition-colors">
                    FAQs
                  </a>
                </li>
                <li>
                  <a href="/contact" className="hover:text-white transition-colors">
                    Help Center
                  </a>
                </li>
              
                <li>
                  <a href="/storage/sitemaps/sitemap-index.xml" className="hover:text-white transition-colors">
                    Sitemap
                  </a>
                </li>
              </ul>
            </div>

            <div className="py-8 md:py-20 px-30">
              <h3 className="font-semibold text-xl mb-10 text-start">Legal</h3>
              <ul className="space-y-3 text-sm text-gray-400 text-start">
                <li>
                  <a href="/pages/privacy-policy" className="hover:text-white transition-colors">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="/pages/terms-of-service" className="hover:text-white transition-colors">
                    Terms of Service
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="font-inter border-t border-t-primary/80 py-16 text-center mt-5">
            <p className="text-sm">
              Copyright © {new Date().getFullYear()}{' '}
              <a href="/login" className="color-primary-gradient">
                SpaceByte Cloud Storage
              </a>
              , All Rights Reserved
            </p>
          </div>
        </div>
      </footer>
    </Fragment>
  );
}

function ComparisonSection() {
  const comparisonRows = [
    {
      title: "Storage Pricing (Cheapest Cloud Storage)",
      gdrive: "Expensive USD pricing + Forex markups",
      space: "Cheapest cloud storage in INR (Starts at ₹20/mo)",
    },
    {
      title: "Upload & Transfer Speed",
      gdrive: "International server routing latency (50-150ms)",
      space: "Sub-15ms Indian domestic fiber IX peering",
    },
    {
      title: "Security & Privacy",
      gdrive: "Platform-side file scanning & ad indexing",
      space: "AES-256 / Zero-Knowledge (Zero AI Scraping)",
    },
    {
      title: "Single File Upload Limit",
      gdrive: "Severely throttled / 750GB daily quota limit",
      space: "100% Uncapped (50GB+ master exports)",
    },
    {
      title: "Data Sovereignty & Compliance",
      gdrive: "Offshore data centers (US/EU)",
      space: "100% DPDP Act Compliant (Indian Data Centers)",
    },
    {
      title: "Free Storage Allocation",
      gdrive: "15 GB (Shared across Gmail, Photos, Docs)",
      space: "25 GB Dedicated High-Speed Storage",
    },
  ];

  return (
    <section className="cmp-section">
      <div className="cmp-inner">
        <div className="cmp-heading">
          <h2 className="font-archivo text-3xl font-bold md:text-4xl lg:text-5xl text-[#111827]">
            Direct Comparison: <span className="color-primary-gradient">SpaceByte</span> vs. Google Drive
          </h2>
          <p className="mt-3 text-gray-600 font-inter">
            See how SpaceByte Cloud storage compares directly against legacy providers in speed, pricing, and data sovereignty.
          </p>
        </div>

        {/* TABLE */}
        <div className="cmp-table-wrap">
          <table className="cmp-table">
            <thead>
              <tr>
                <th className="cmp-th-feat">Feature / Capability</th>
                <th className="cmp-th-gdrive">
                  <span>Google Drive</span>
                </th>
                <th className="cmp-th-space">
                  <div className="cmp-th-brand">
                    <span>SpaceByte Cloud</span>
                    <span className="cmp-winner-badge font-archivo">WINNER</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row, index) => (
                <tr key={index}>
                  <td className="cmp-td-feat font-archivo">{row.title}</td>
                  <td className="cmp-td-gdrive font-inter">
                    <div className="cmp-val-wrap">
                      <span className="cmp-cross">✕</span>
                      <span>{row.gdrive}</span>
                    </div>
                  </td>
                  <td className="cmp-td-space font-inter">
                    <div className="cmp-val-wrap">
                      <span className="cmp-check">✓</span>
                      <span>{row.space}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function CalculateDialogTrigger() {
  const form = useForm<ContactPagePayload>({
    defaultValues: {
      message: '',
    },
  });
  const [isOpen, setIsOpen] = useState(false);
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const submitForm = useSubmitContactForm(form);
  const { verify } = useRecaptcha('contact');

  return (
    <>
      <DialogTrigger type="modal" isOpen={isOpen} onOpenChange={setIsOpen}>
        <button className="btn w-full rounded-lg !text-white font-semibold !bg-gradient-to-r !from-[#0072ff] !to-[#00c6ff] shadow-lg hover:shadow-xl transition">
          <span>Get Quote</span>
        </button>
        <Dialog className="w-full max-w-lg rounded-xl p-6">
          <DialogHeader className="font-archivo text-2xl font-semibold" showDivider>
            <Trans message="Storage Cost Calculator" />
          </DialogHeader>
          <DialogBody>
            <div className="font-inter flex items-center justify-center text-center">
              Enter storage size (whole number greater than 5 TB) to calculate monthly cost.
            </div>
            <Form
              form={form}
              className="mt-4 flex flex-col items-center text-center"
              onSubmit={async (value) => {
                calculateCost(value);
              }}
            >
              <FormTextField
                label={<Trans message="Enter Storage in TB" />}
                name="tb"
                required
                type="number"
                className="mb-6 w-full rounded-lg p-2"
              />
              <div className="text-red-500 text-md" id="errorMsg"></div>
              <div className="text-blue-500 text-md" id="resultOutput"></div>
              <div className="mt-6 flex w-full justify-between gap-4">
                <button className="btn-warning mt-16" type="submit">
                  Calculate
                </button>
                <Button
                  color="positive"
                  variant="outline"
                  className="mt-16 rounded-lg"
                  type="button"
                  onClick={() => {
                    const resultOutput = document.getElementById('resultOutput')?.textContent;
                    form.setValue('message', resultOutput || '');
                    setIsOpen(false);
                    setIsContactDialogOpen(true);
                  }}
                >
                  Contact Us
                </Button>
              </div>
            </Form>
          </DialogBody>
        </Dialog>
      </DialogTrigger>

      {isContactDialogOpen && (
        <DialogTrigger type="modal" isOpen={isContactDialogOpen} onOpenChange={setIsContactDialogOpen}>
          <Dialog className="w-full max-w-lg rounded-xl p-6">
            <DialogHeader className="font-archivo text-2xl font-semibold" showDivider>
              <Trans message="Contact Us" />
            </DialogHeader>
            <DialogBody>
              {submitForm.isPending ? (
                <FullPageLoader />
              ) : (
                <Form
                  form={form}
                  className="flex flex-col gap-4"
                  onSubmit={async (value) => {
                    const isValid = await verify();
                    if (isValid) {
                      submitForm.mutate(value, {
                        onSuccess: () => {
                          setIsContactDialogOpen(false);
                        },
                      });
                    }
                  }}
                >
                  <FormTextField label={<Trans message="Name" />} name="name" required className="rounded-lg p-2" />
                  <FormTextField label={<Trans message="Email" />} name="email" required type="email" className="rounded-lg p-2" />
                  <FormTextField label={<Trans message="Mobile Number" />} name="mobileNumber" required type="number" className="rounded-lg p-2" />
                  <FormTextField
                    label={<Trans message="Message" />}
                    name="message"
                    required
                    inputElementType="textarea"
                    className="rounded-lg p-2"
                    rows={5}
                  />

                  <div className="mt-4 flex w-full justify-between gap-4">
                    <Button variant="flat" onClick={() => setIsContactDialogOpen(false)} className="rounded-lg px-4 py-2 text-lg">
                      <Trans message="Back" />
                    </Button>
                    <Button type="submit" variant="flat" color="primary" className="rounded-lg px-4 py-2 text-lg">
                      <Trans message="Send Message" />
                    </Button>
                  </div>
                </Form>
              )}
            </DialogBody>
          </Dialog>
        </DialogTrigger>
      )}
    </>
  );
}

function calculateCost({ tb }: any) {
  const tbInput: any = tb;
  const errorMsg: any = document.getElementById('errorMsg');
  const resultOutput: any = document.getElementById('resultOutput');

  if (!errorMsg || !resultOutput) return;

  errorMsg.textContent = '';
  resultOutput.textContent = '';

  if (!/^\d+$/.test(tbInput)) {
    errorMsg.textContent = 'Error: Storage must be a whole number!';
    return;
  }

  const tbValue = parseInt(tbInput, 10);

  if (tbValue <= 5) {
    errorMsg.textContent = 'Error: Storage must be greater than 5 TB!';
    return;
  }

  const gbStorage = tbValue * 1024;
  const cost = gbStorage * 0.15;

  resultOutput.innerHTML = `
      Storage: <strong>${tbValue.toLocaleString()} TB</strong><br>
      Total Cost: <strong>₹${cost.toFixed(2)}/month</strong><br>
      GST and Convenience Fee Extra**
  `;
}

function useCustomStyles(): void {
  useEffect(() => {
    if (!document.getElementById("best-cloud-css")) {
      const s = document.createElement("style");
      s.id = "best-cloud-css";
      s.textContent = `
        .sb-roles {
          display: grid;
          grid-template-columns: 320px 1fr;
          border-bottom: 2px solid #1E3A8A;
          border-top: 2px solid #1E3A8A;
          min-height: 420px;
        }
        .sb-roles-left {
          border-right: 2px solid #1E3A8A;
          padding: 3.5rem 2.5rem;
          background: #1E3A8A;
          color: #FDFCFA;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }
        .sb-roles-left h2 {
          font-size: 2rem;
          font-weight: 900;
          line-height: 1.15;
          color: #FDFCFA;
        }
        .sb-rl-count {
          font-size: 3.5rem;
          font-weight: 900;
          color: #00c2ff;
          line-height: 1;
        }
        .sb-roles-list {
          display: flex;
          flex-direction: column;
          background: #f5f8ff;
        }
        .sb-role-row {
          display: flex;
          align-items: center;
          border-bottom: 1px solid rgba(28,27,24,0.12);
          transition: background .2s;
        }
        .sb-role-row:last-child {
          border-bottom: none;
        }
        .sb-role-row:hover {
          background: #e3eaf9;
        }
        .sb-rr-num {
          width: 60px;
          flex-shrink: 0;
          border-right: 1px solid rgba(28,27,24,0.12);
          text-align: center;
          padding: 1.8rem 0;
          font-size: .75rem;
          font-weight: 700;
          color: #8C8880;
        }
        .sb-rr-icon {
          width: 64px;
          flex-shrink: 0;
          border-right: 1px solid rgba(28,27,24,0.12);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1.8rem 0;
        }
        .sb-rr-body {
          flex: 1;
          padding: 1.8rem 2rem;
          min-width: 0;
        }
        .sb-rr-title {
          font-size: 1rem;
          font-weight: 700;
          color: #1C1B18;
          margin-bottom: .25rem;
        }
        .sb-rr-desc {
          font-size: .85rem;
          color: #64748B;
          line-height: 1.5;
        }
        .sb-rr-arrow {
          padding: 0 1.5rem;
          font-size: 1.2rem;
          color: #0072ff;
          transition: transform .2s;
        }
        .sb-role-row:hover .sb-rr-arrow {
          transform: translateX(4px);
        }

        .cmp-section {
          position: relative;
          overflow: hidden;
          background: #f5f8ff;
          padding: 5rem 1rem;
          color: #000;
        }
        .cmp-inner {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1rem;
        }
        .cmp-heading {
          text-align: center;
          margin-bottom: 3.5rem;
        }
        .cmp-heading h2 {
          font-family: 'Archivo', sans-serif;
          font-size: clamp(1.6rem, 4vw, 3rem);
          font-weight: 700;
          line-height: 1.2;
          margin-bottom: 1rem;
        }
        .cmp-heading h2 .gradient-text {
          background: linear-gradient(90deg, #0072ff, #00c6ff);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .cmp-heading p {
          font-family: 'Inter', sans-serif;
          color: #6b7280;
          font-size: 0.95rem;
          max-width: 600px;
          margin: 0 auto;
          line-height: 1.7;
        }
        .cmp-logo-row {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 20px;
          margin-bottom: 2.5rem;
        }
        .cmp-logo-card {
          width: 110px;
          height: 110px;
          border-radius: 20px;
          background: #ffffff;
          display: flex;
          justify-content: center;
          align-items: center;
          box-shadow: 0 10px 30px rgba(0,0,0,0.08);
          overflow: hidden;
        }
        .cmp-logo-card img {
          width: 80%;
          height: 80%;
          object-fit: contain;
        }
        .cmp-vs {
          width: 46px;
          height: 46px;
          border-radius: 50%;
          background: linear-gradient(135deg, #1E3A8A, #3B82F6);
          color: #fff;
          font-weight: 700;
          font-size: 0.82rem;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .cmp-table-wrap {
          max-width: 1000px;
          margin: 0 auto;
          border-radius: 16px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.08);
          background: #fff;
          overflow: hidden;
        }
        .cmp-table {
          width: 100%;
          border-collapse: collapse;
        }
        .cmp-table th {
          padding: 1.2rem 1.5rem;
          font-size: 0.95rem;
          font-weight: 700;
          border-bottom: 2px solid #e2e8f0;
          text-align: left;
        }
        .cmp-th-feat {
          background: #f8fafc;
          width: 35%;
        }
        .cmp-th-gdrive {
          background: #ffffff;
          width: 32%;
        }
        .cmp-th-space {
          background: #eef6ff;
          width: 33%;
        }
        .cmp-th-brand {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .cmp-th-brand img {
          width: 24px;
          height: 24px;
          object-fit: contain;
        }
        .cmp-winner-badge {
          background: linear-gradient(90deg, #0072ff, #00c6ff);
          color: #fff;
          font-size: 0.65rem;
          font-weight: 800;
          padding: 2px 8px;
          border-radius: 20px;
          margin-left: auto;
        }
        .cmp-table td {
          padding: 1.1rem 1.5rem;
          border-bottom: 1px solid #f1f5f9;
          font-size: 0.9rem;
        }
        .cmp-td-feat {
          font-weight: 700;
          color: #1e293b;
          background: #f8fafc;
        }
        .cmp-td-gdrive {
          color: #64748b;
          background: #ffffff;
        }
        .cmp-td-space {
          font-weight: 600;
          color: #0072ff;
          background: #f0f7ff;
        }
        .cmp-val-wrap {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .cmp-cross {
          color: #ef4444;
          font-weight: bold;
        }
        .cmp-check {
          color: #10b981;
          font-weight: bold;
        }

        @media (max-width: 860px) {
          .sb-roles { grid-template-columns: 1fr; }
          .sb-roles-left { border-right: none; border-bottom: 2px solid #1E3A8A; }
        }
      `;
      document.head.appendChild(s);
    }
  }, []);
}
